import { getCurrencyByCode } from '@/constants/currencies'

/**
 * Money formatting. App-wide, every amount is an integer in **hundredths**,
 * independent of a currency's actual fraction digits — so display precision
 * comes from the currency, not a hard-coded `.toFixed(2)`.
 */

/** Decimal-string amount from a form input (e.g. "12.50") to integer cents. */
export const toCents = (amount: string): number => Math.round(parseFloat(amount || '0') * 100)

/** Integer cents to a 2-decimal input string (e.g. 1250 -> "12.50"). */
export const fromCents = (cents: number): string => (cents / 100).toFixed(2)

const decimalsCache = new Map<string, number>()

/** ISO 4217 fraction digits via `Intl`, falling back to 2. Memoized. */
export const getCurrencyDecimals = (currencyCode: string): number => {
	const key = currencyCode.toLowerCase()
	const cached = decimalsCache.get(key)
	if (cached !== undefined) return cached
	let decimals = 2
	try {
		decimals =
			new Intl.NumberFormat('en', {
				style: 'currency',
				currency: currencyCode.toUpperCase(),
			}).resolvedOptions().maximumFractionDigits ?? 2
	} catch {
		decimals = 2
	}
	decimalsCache.set(key, decimals)
	return decimals
}

/**
 * Fraction digits to *display*. Storage is 2-decimal, so currencies with more
 * minor-unit digits (BHD/KWD → 3) clamp down rather than render a phantom zero.
 */
export const getDisplayDecimals = (currencyCode: string): number =>
	Math.min(getCurrencyDecimals(currencyCode), 2)

/**
 * Bare decimal string — no symbol or separators — for machine-readable output
 * (CSV). Sign is preserved (`-3333, 'usd'` → "-33.33").
 */
export const formatMoneyPlain = (amount: number, currencyCode: string): string =>
	(amount / 100).toFixed(getDisplayDecimals(currencyCode))

/**
 * Format a stored amount with its currency symbol. Unknown codes fall back to a
 * code suffix ("42.00 ZZZ") rather than a `$` that could misstate the currency.
 * Caller controls sign. e.g. (4200, 'usd') → "$42.00"; (10000, 'jpy') → "¥100".
 */
export const formatMoney = (amount: number, currencyCode: string): string => {
	const currency = getCurrencyByCode(currencyCode)
	const value = (amount / 100).toFixed(getDisplayDecimals(currencyCode))
	if (!currency) return currencyCode ? `${value} ${currencyCode.toUpperCase()}` : value
	return currency.isSymbolOnLeft ? `${currency.symbol}${value}` : `${value}${currency.symbol}`
}
